if (!customElements.get('variant-cards')) {
  customElements.define(
    'variant-cards',
    class VariantCards extends HTMLElement {
      constructor() {
        super();
        this.variantFormId = null;
        this.sectionId = this.dataset.section;
        this.updateUrl = this.dataset.updateUrl !== 'false';
      }

      connectedCallback() {
        // Check if we're on a product page before initializing
        if (!document.body.classList.contains('product-template')) {
          console.debug('[variant-cards] Not on a product page, skipping initialization');
          return;
        }
        
        // Get product data to check for the variant-bundle tag
        const productData = this.getProductJSON();
        if (productData && productData.tags) {
          const hasVariantBundleTag = productData.tags.some(tag => 
            typeof tag === 'string' && tag.toLowerCase() === 'variant-bundle'
          );
          
          if (!hasVariantBundleTag) {
            console.debug('[variant-cards] Product does not have variant-bundle tag, skipping initialization');
            return;
          }
        }
        
        // Find the cards and variant input
        this.cards = Array.from(this.querySelectorAll('.pricing-card'));
        console.debug('[variant-cards] Found cards', { count: this.cards.length });
        this.variantInput = null;
        
        // Wait for DOM to be ready before finding form elements
        setTimeout(() => {
          this.initializeCards();
          
          // Check for variant in URL and select that card if it exists
          const urlParams = new URLSearchParams(window.location.search);
          const variantId = urlParams.get('variant');
          
          if (variantId) {
            const cardForVariant = Array.from(this.cards).find(card => 
              card.dataset.variantId === variantId
            );
            
            if (cardForVariant) {
              this.handleCardClick({ currentTarget: cardForVariant });
            }
          }
        }, 0);
      }
      
      /**
       * Clean up any observers when the component is removed from the DOM
       */
      disconnectedCallback() {
        // Clean up selling plan observer
        if (this.sellingPlanObserver) {
          this.sellingPlanObserver.disconnect();
          console.debug('[variant-cards] Disconnected selling plan observer');
        }
        
        // Clean up form observer
        if (this.formObserver) {
          this.formObserver.disconnect();
          console.debug('[variant-cards] Disconnected form observer');
        }
        
        // Clear any intervals
        if (this.sellingPlanSyncInterval) {
          clearInterval(this.sellingPlanSyncInterval);
          console.debug('[variant-cards] Cleared selling plan sync interval');
        }
      }

      initializeCards() {
        // Find the form and variant input
        this.productForm = document.querySelector(`#product-form-${this.sectionId}`);
        if (!this.productForm) {
          console.warn('VariantCards: Product form not found', this.sectionId);
          return;
        }
        
        this.variantInput = this.productForm.querySelector('input[name="id"]');
        if (!this.variantInput) {
          console.warn('VariantCards: Variant input not found in form');
          return;
        }

        // Add click and keyboard listeners to cards with ARIA attributes
        this.cards.forEach(card => {
          // Add role and accessibility attributes
          card.setAttribute('role', 'button');
          card.setAttribute('tabindex', '0');
          card.setAttribute('aria-pressed', card.classList.contains('active') ? 'true' : 'false');
          
          // Add event listeners
          card.addEventListener('click', this.handleCardClick.bind(this));
          card.addEventListener('keydown', this.handleCardKeyDown.bind(this));
        });

        // Set initial variant if none is active
        const activeCard = this.querySelector('.pricing-card.active');
        if (!activeCard && this.cards.length > 0) {
          this.cards[0].classList.add('active');
          this.cards[0].setAttribute('aria-pressed', 'true');
          this.updateVariantInput(this.cards[0].dataset.variantId);
        }
        
        // Set up continuous Recharge selling plan monitoring
        this.observeSellingPlanChanges();
      }
      
      /**
       * Sets up robust monitoring of selling plan changes in the main form
       * to ensure the sticky ATC form always has the correct selling plan
       */
      observeSellingPlanChanges() {
        try {
          // Wait for a short delay to ensure Recharge has initialized
          setTimeout(() => {
            this.initializeSellingPlanSync();
          }, 500);
          
          // Also set up a backup timer to periodically check in case Recharge loads later
          this.sellingPlanSyncInterval = setInterval(() => {
            const mainForm = document.querySelector(`#product-form-${this.sectionId}`);
            const mainInput = mainForm?.querySelector('[name="selling_plan"]');
            const stickyATC = document.querySelector('sticky-atc-bar');
            const stickyForm = stickyATC?.querySelector('form');
            const stickyInput = stickyForm?.querySelector('[name="selling_plan"]');
            
            // If main input exists but we don't have a sync set up yet, initialize it
            if (mainInput && !this.sellingPlanObserver) {
              console.debug('[variant-cards] Delayed detection of selling_plan input - initializing sync');
              this.initializeSellingPlanSync();
            }
            
            // If both inputs exist but values don't match, sync them
            if (mainInput && stickyInput && mainInput.value !== stickyInput.value) {
              stickyInput.value = mainInput.value;
              console.debug('[variant-cards] Interval sync of selling_plan', {
                value: mainInput.value || '(empty)',
                from: mainForm.id || 'main-form',
                to: stickyForm.id || 'sticky-form'
              });
            }
          }, 2000);
        } catch (e) {
          console.error('[variant-cards] Error in observeSellingPlanChanges:', e);
        }
      }
      
      /**
       * Initializes the MutationObserver and performs initial sync of selling plan
       */
      initializeSellingPlanSync() {
        try {
          // Find the main form
          const mainForm = document.querySelector(`#product-form-${this.sectionId}`);
          if (!mainForm) {
            console.debug('[variant-cards] Main product form not found for selling plan observer');
            return;
          }
          
          // Find the sticky ATC bar
          const stickyATC = document.querySelector('sticky-atc-bar');
          if (!stickyATC) {
            console.debug('[variant-cards] Sticky ATC bar not found for selling plan sync');
            return;
          }
          
          // Find the sticky ATC form
          const stickyForm = stickyATC.querySelector('form');
          if (!stickyForm) {
            console.warn('[variant-cards] Sticky ATC form not found for selling plan sync');
            return;
          }
          
          // Check for existing selling plan input or create one if needed
          let stickyInput = stickyForm.querySelector('[name="selling_plan"]');
          if (!stickyInput) {
            stickyInput = document.createElement('input');
            stickyInput.type = 'hidden';
            stickyInput.name = 'selling_plan';
            stickyForm.appendChild(stickyInput);
            console.debug('[variant-cards] Created selling_plan input in sticky ATC form');
          }
          
          // Find main form's selling plan input - which may or may not exist yet
          let mainInput = mainForm.querySelector('[name="selling_plan"]');
          
          // If main input exists, do initial sync
          if (mainInput) {
            // Initial sync
            stickyInput.value = mainInput.value;
            console.debug('[variant-cards] Initial sync of selling_plan input', {
              value: mainInput.value || '(empty)',
              from: mainForm.id || 'main-form',
              to: stickyForm.id || 'sticky-form'
            });
            
            // Set up observer on the existing input
            this.setupSellingPlanObserver(mainInput, stickyInput, mainForm);
          } else {
            console.debug('[variant-cards] No selling_plan input found in main form yet - will observe for creation');
          }
          
          // Additionally, observe the form for new inputs that might be added dynamically
          const formObserver = new MutationObserver((mutations) => {
            // Check if a selling_plan input was added
            for (const mutation of mutations) {
              if (mutation.type === 'childList') {
                const addedNodes = Array.from(mutation.addedNodes);
                for (const node of addedNodes) {
                  // Check if the node itself is a selling_plan input
                  if (node.nodeName === 'INPUT' && node.name === 'selling_plan') {
                    mainInput = node;
                    break;
                  }
                  
                  // Check if the node contains a selling_plan input
                  if (node.nodeType === 1) { // Element node
                    const inputInNode = node.querySelector('[name="selling_plan"]');
                    if (inputInNode) {
                      mainInput = inputInNode;
                      break;
                    }
                  }
                }
                
                // If we found a newly added selling_plan input, set up syncing
                if (mainInput && !this.sellingPlanObserver) {
                  console.debug('[variant-cards] Detected newly added selling_plan input', {
                    value: mainInput.value || '(empty)'
                  });
                  
                  // Initial sync
                  stickyInput.value = mainInput.value;
                  
                  // Set up observer
                  this.setupSellingPlanObserver(mainInput, stickyInput, mainForm);
                }
              }
            }
          });
          
          // Start observing the form for new inputs
          formObserver.observe(mainForm, {
            childList: true,
            subtree: true
          });
          
          // Store reference for cleanup
          this.formObserver = formObserver;
          
          console.debug('[variant-cards] Selling plan sync setup complete');
        } catch (e) {
          console.error('[variant-cards] Error in initializeSellingPlanSync:', e);
        }
      }
      
      /**
       * Sets up an observer on the main selling plan input
       * @param {HTMLInputElement} mainInput - The main form's selling_plan input
       * @param {HTMLInputElement} stickyInput - The sticky form's selling_plan input
       * @param {HTMLFormElement} mainForm - The main product form
       */
      setupSellingPlanObserver(mainInput, stickyInput, mainForm) {
        try {
          // Clean up any existing observer
          if (this.sellingPlanObserver) {
            this.sellingPlanObserver.disconnect();
          }
          
          // Create new observer to watch for value changes
          const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
              // For attribute changes on the input itself
              if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                stickyInput.value = mainInput.value;
                console.debug('[variant-cards] Synced selling_plan via attribute change', {
                  value: mainInput.value || '(empty)'
                });
              }
              
              // When the value changes in other ways (like via JavaScript)
              if (mutation.type === 'characterData' || mutation.type === 'childList') {
                stickyInput.value = mainInput.value;
                console.debug('[variant-cards] Synced selling_plan via value change', {
                  value: mainInput.value || '(empty)'
                });
              }
            }
          });
          
          // Observe the input for direct attribute changes
          observer.observe(mainInput, {
            attributes: true,
            attributeFilter: ['value'],
            characterData: true,
            childList: true,
            subtree: true
          });
          
          // Store reference for cleanup
          this.sellingPlanObserver = observer;
          
          // Also set up an event listener for input changes
          mainInput.addEventListener('change', () => {
            const wasRemoved = mainInput.value === '' && stickyInput.value !== '';
            stickyInput.value = mainInput.value;
            console.debug('[variant-cards] Synced selling_plan via change event', {
              value: mainInput.value || '(empty)',
              wasRemoved
            });
            
            // Find sticky ATC to update price
            if (wasRemoved) {
              const stickyATC = document.querySelector('sticky-atc-bar');
              if (stickyATC && typeof stickyATC.updatePrice === 'function') {
                console.debug('[variant-cards] Forcing price update after selling plan removed');
                setTimeout(() => {
                  stickyATC.updatePrice();
                  
                  // Additional call to updatePriceFromSellingPlan to ensure proper reset
                  if (typeof stickyATC.updatePriceFromSellingPlan === 'function') {
                    stickyATC.updatePriceFromSellingPlan();
                  }
                }, 100);
              }
            }
          });
          
          console.debug('[variant-cards] Selling plan observer initialized on input');
        } catch (e) {
          console.error('[variant-cards] Error setting up selling plan observer:', e);
        }
      }
      
      handleCardKeyDown(event) {
        // Handle Enter or Space key
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.handleCardClick(event);
        }
      }

      handleCardClick(event) {
        const clickedCard = event.currentTarget;
        const variantId = clickedCard.dataset.variantId;
        
        // Don't proceed if already active
        if (clickedCard.classList.contains('active')) return;
        
        try {
          // Update active state and ARIA attributes
          this.cards.forEach(card => {
            const isActive = card === clickedCard;
            card.classList.toggle('active', isActive);
            card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
          
          // Update variant input and notify the product form
          this.updateVariantInput(variantId);
          
          // Get the selected variant data with complete information
          const selectedVariantData = this.getSelectedVariantData(variantId);
          
          if (!selectedVariantData) {
            console.warn('[variant-cards] Could not get variant data for', variantId);
            return;
          }
          
          // Ensure the available property is set properly for sticky ATC
          if (selectedVariantData.available === undefined) {
            // Default to true if we can't determine (assumes in stock unless explicitly marked as out of stock)
            selectedVariantData.available = true;
            
            // Check inventory if possible
            if (selectedVariantData.inventory_quantity !== undefined) {
              selectedVariantData.available = selectedVariantData.inventory_quantity > 0;
            }
            
            // If inventory policy is continue, it's always available
            if (selectedVariantData.inventory_policy === 'continue') {
              selectedVariantData.available = true;
            }
            
            console.debug('[variant-cards] Set available property:', {
              variantId: selectedVariantData.id,
              available: selectedVariantData.available,
              inventory_quantity: selectedVariantData.inventory_quantity,
              inventory_policy: selectedVariantData.inventory_policy
            });
          }
          
          // Publish event for other components
          this.publishVariantChangeEvent(selectedVariantData);
          
          // Directly update the sticky ATC as a fallback
          this.updateStickyATC(selectedVariantData);
          
          // Update URL if needed
          if (this.updateUrl) {
            this.updateBrowserHistory(variantId);
          }
        } catch (e) {
          console.error('Error in handleCardClick:', e);
        }
      }

      /**
       * Updates the hidden variant input with the selected variant ID
       * @param {string|number} variantId - The ID of the selected variant
       */
      updateVariantInput(variantId) {
        if (!this.variantInput) {
          console.warn('[variant-cards] Variant input not found');
          return;
        }
        
        // Set the value
        this.variantInput.value = variantId;
        
        // Log before dispatching event
        console.debug('[variant-cards] Updated variant input', {
          variantId: variantId,
          inputElement: this.variantInput.id || '(no id)',
          form: this.variantInput.closest('form')?.id || '(no form)'
        });
        
        // Dispatch change event to notify form
        try {
          const changeEvent = new Event('change', { bubbles: true });
          this.variantInput.dispatchEvent(changeEvent);
          
          // Also update all other variant inputs with the same name across the page
          // This helps with syncing main form and sticky form
          document.querySelectorAll(`input[name="${this.variantInput.name}"]`).forEach(input => {
            if (input !== this.variantInput) {
              input.value = variantId;
              input.dispatchEvent(new Event('change', { bubbles: true }));
              console.debug('[variant-cards] Synced additional variant input', {
                element: input.id || input.name,
                form: input.closest('form')?.id || '(no form)'
              });
            }
          });
        } catch (e) {
          console.error('[variant-cards] Error dispatching change event:', e);
        }
      }

      /**
       * Gets the complete product JSON data from the page
       * @returns {Object|null} The complete product data or null if not found
       */
      getProductJSON() {
        try {
          // Simplified product JSON lookup focusing on most reliable selectors
          const productJsonElement = 
            document.getElementById(`ProductJSON-${this.sectionId}`) || 
            document.querySelector('[id^="ProductJSON-"]');
          
          if (productJsonElement && productJsonElement.textContent) {
            try {
              const parsed = JSON.parse(productJsonElement.textContent);
              console.debug('[variant-cards] Found product JSON data', { 
                source: productJsonElement.id || 'unknown',
                hasVariants: !!(parsed && parsed.variants && parsed.variants.length),
                hasTags: !!(parsed && parsed.tags && parsed.tags.length)
              });
              return parsed;
            } catch (e) {
              console.warn('[variant-cards] Error parsing product JSON:', e);
            }
          }
          
          console.warn('[variant-cards] Product JSON not found', {
            sectionId: this.sectionId,
            availableScripts: Array.from(document.querySelectorAll('script[type="application/json"]')).map(s => s.id)
          });
          return null;
        } catch (e) {
          console.error('[variant-cards] Error in getProductJSON:', e);
          return null;
        }
      }
      
      /**
       * Gets the full data for a selected variant
       * @param {string|number} variantId - The ID of the variant to retrieve
       * @returns {Object|null} The complete variant data or null if not found
       */
      getSelectedVariantData(variantId) {
        try {
          // Normalize variantId to number for comparison
          const numericVariantId = Number(variantId);
          
          // Try to get complete product data
          const productData = this.getProductJSON();
          
          if (productData && productData.variants && productData.variants.length) {
            // Find the selected variant in the complete product data
            const variant = productData.variants.find(v => Number(v.id) === numericVariantId);
            
            if (variant) {
              // Ensure critical properties exist (sticky ATC needs these)
              if (variant.available === undefined) {
                // Default to true (in stock) if we can't determine
                variant.available = true;
                
                // Check inventory if possible
                if (variant.inventory_quantity !== undefined) {
                  variant.available = variant.inventory_quantity > 0;
                  
                  // If inventory policy is continue, it's always available
                  if (variant.inventory_policy === 'continue') {
                    variant.available = true;
                  }
                }
                
                console.debug('[variant-cards] Set missing available property on product JSON variant:', variant.available);
              }
              
              console.debug('[variant-cards] Found variant in product JSON', {
                variantId: numericVariantId,
                available: variant.available,
                price: variant.price,
                hasComparePrice: !!variant.compare_at_price
              });
              
              return variant;
            }
          }
          
          console.debug('[variant-cards] Using fallback variant resolution method');
          
          // Fallback to original implementation
          const variantScript = this.querySelector('[data-selected-variant]');
          if (!variantScript) {
            console.warn('[variant-cards] No [data-selected-variant] script found for fallback');
          }
          
          try {
            let fallbackVariant = null;
            
            // Try to get from data-selected-variant script
            if (variantScript) {
              const selectedVariant = JSON.parse(variantScript.textContent);
              const allVariants = this.getAllVariants();
              
              // Find the selected variant
              fallbackVariant = allVariants.find(variant => Number(variant.id) === numericVariantId) || selectedVariant;
            } else {
              // Last resort: build a minimal variant object from card data
              const activeCard = Array.from(this.cards).find(card => 
                Number(card.dataset.variantId) === numericVariantId
              );
              
              if (activeCard) {
                fallbackVariant = {
                  id: numericVariantId,
                  price: Number(activeCard.dataset.price) || 0,
                  title: activeCard.querySelector('.variant-card__title')?.textContent?.trim() || 'Unknown Variant',
                  options: [],
                  available: true, // Default to available
                  inventory_quantity: 10, // Default to in stock
                  inventory_policy: 'continue', // Default to continue
                  requires_shipping: true
                };
                
                // Add compare_at_price if available
                if (activeCard.dataset.compareAtPrice) {
                  fallbackVariant.compare_at_price = Number(activeCard.dataset.compareAtPrice);
                }
              } else {
                // Ultra-fallback with minimal data
                fallbackVariant = {
                  id: numericVariantId,
                  title: 'Variant',
                  price: 0,
                  options: [],
                  available: true,
                  inventory_quantity: 10,
                  inventory_policy: 'continue',
                  requires_shipping: true
                };
              }
            }
            
            // If we found a fallback variant, ensure it has all required properties
            if (fallbackVariant) {
              // Ensure the variant has an 'available' property (default to true)
              if (fallbackVariant.available === undefined) {
                fallbackVariant.available = true;
              }
              
              // Ensure other critical properties exist
              if (fallbackVariant.inventory_quantity === undefined) {
                fallbackVariant.inventory_quantity = 10;
              }
              
              if (fallbackVariant.inventory_policy === undefined) {
                fallbackVariant.inventory_policy = 'continue';
              }
              
              console.debug('[variant-cards] Using fallback variant data', {
                source: variantScript ? 'data-selected-variant' : 'card-data',
                variantId: fallbackVariant.id,
                available: fallbackVariant.available,
                inventory_quantity: fallbackVariant.inventory_quantity,
                inventory_policy: fallbackVariant.inventory_policy
              });
              
              return fallbackVariant;
            }
            
            console.warn('[variant-cards] Could not create fallback variant data');
            return null;
          } catch (e) {
            console.error('[variant-cards] Error parsing variant data:', e);
            return null;
          }
        } catch (e) {
          console.error('[variant-cards] Error in getSelectedVariantData:', e);
          return null;
        }
      }
      
      getAllVariants() {
        // This is a simplified implementation since we don't have access to all variants
        // In a real implementation, this would fetch all variants from a data attribute or the API
        if (!this.cards || !Array.isArray(this.cards) || this.cards.length === 0) {
          console.warn('[variant-cards] No cards found or cards is not an array', {
            cards: this.cards,
            isArray: Array.isArray(this.cards),
            length: this.cards?.length
          });
          return [];
        }
        
        return this.cards.map(card => ({
          id: Number(card.dataset.variantId),
          price: Number(card.dataset.price) || 0,
          // Add essential properties that sticky ATC needs
          available: true, // Default to available
          inventory_quantity: 10, // Default to in stock
          inventory_policy: 'continue', // Default to continue selling when out of stock
          requires_shipping: true, // Default to requiring shipping
          options: [], // Empty options array
          title: card.querySelector('.variant-card__title')?.textContent?.trim() || 
                 card.querySelector('h3')?.textContent?.trim() || 
                 'Variant'
        }));
      }

      /**
       * Gets the section ID that should be used for events
       * @returns {string} The correct section ID for events
       */
      getEventSectionId() {
        try {
          // Try to find product-info element that sticky ATC uses
          const productInfo = document.querySelector('product-info');
          
          if (productInfo && productInfo.dataset.section) {
            return productInfo.dataset.section;
          }
          
          // Fallback to our own section ID
          return this.sectionId;
        } catch (e) {
          console.error('Error in getEventSectionId:', e);
          return this.sectionId;
        }
      }

      /**
       * Publishes variant change events for other components
       * @param {Object} variantData - The complete variant data
       */
      publishVariantChangeEvent(variantData) {
        if (!variantData) return;
        
        try {
          // Get the correct section ID for events
          const eventSectionId = this.getEventSectionId();
          
          // Perform a quick validation of the variant object
          const validationIssues = [];
          if (variantData.available === undefined) {
            console.warn('[variant-cards] Variant is missing required property: available');
            validationIssues.push('missing available');
            // Ensure it exists
            variantData.available = true;
          }
          
          if (variantData.price === undefined) {
            console.warn('[variant-cards] Variant is missing required property: price');
            validationIssues.push('missing price');
          }
          
          // Publish event similar to variant-selects.js for compatibility
          if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
            const eventData = {
              data: {
                sectionId: eventSectionId,
                variant: variantData,
                // We don't have HTML context, but include a placeholder to match structure
                html: null
              }
            };
            
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, eventData);
            
            console.debug('[variant-cards] Published variantChange event', {
              sectionId: eventSectionId,
              variantId: variantData.id,
              available: variantData.available,
              price: variantData.price,
              hasIssues: validationIssues.length > 0 ? validationIssues : false,
              variant: variantData
            });
          }
          
          // Also dispatch standard event for broader compatibility
          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variantData
              }
            })
          );
        } catch (e) {
          console.error('Error in publishVariantChangeEvent:', e);
          
          // Fallback to original implementation
          if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                variant: variantData,
              }
            });
          }
          
          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variantData
              }
            })
          );
        }
      }

      /**
       * Updates any additional UI elements to reflect the selected variant
       * @param {Object} variantData - The complete variant data
       */
      updateVariantSelectionUI(variantData) {
        if (!variantData) return;
        
        try {
          // Update price if we have a price element
          this.updatePriceUI(variantData);
          
          // Try to sync with the native variant selectors if present
          this.syncWithNativeVariantSelects(variantData);
        } catch (e) {
          console.error('[variant-cards] Error in updateVariantSelectionUI:', e);
        }
      }
      
      /**
       * Updates price UI elements if they exist
       * @param {Object} variantData - The variant data containing price information
       */
      updatePriceUI(variantData) {
        try {
          // Find price elements (implementation depends on theme structure)
          const priceElement = document.querySelector('.product__price');
          
          if (priceElement && variantData.price) {
            // Format price according to theme's currency format
            const formattedPrice = typeof FoxTheme !== 'undefined' && FoxTheme.Currency
              ? FoxTheme.Currency.formatMoney(variantData.price, FoxTheme.settings.moneyFormat)
              : `$${(variantData.price / 100).toFixed(2)}`;
              
            // Update price display
            priceElement.innerHTML = formattedPrice;
            
            console.debug('[variant-cards] Updated price UI', {
              price: formattedPrice,
              originalPrice: variantData.price
            });
          }
        } catch (e) {
          console.error('[variant-cards] Error updating price UI:', e);
        }
      }
      
      /**
       * Synchronizes with native variant select elements if they exist
       * @param {Object} variantData - The complete variant data
       */
      syncWithNativeVariantSelects(variantData) {
        try {
          // Find the native variant selects component
          const variantSelects = document.querySelector('variant-selects');
          if (!variantSelects) {
            console.debug('[variant-cards] No native variant-selects found for syncing');
            return;
          }
          
          // Find all option elements
          const selectElements = variantSelects.querySelectorAll('select');
          if (!selectElements.length) {
            console.debug('[variant-cards] No select elements found in variant-selects');
            return;
          }
          
          // Check if we have option values in the variant data
          if (!variantData.options || !Array.isArray(variantData.options)) {
            console.warn('[variant-cards] Variant data missing options array:', variantData);
            return;
          }
          
          // Update each select with the corresponding option value
          selectElements.forEach((select, index) => {
            if (index < variantData.options.length) {
              const optionValue = variantData.options[index];
              
              // Find the option element with matching value
              const matchingOption = Array.from(select.options).find(option => 
                option.value === optionValue || option.innerText.trim() === optionValue
              );
              
              if (matchingOption) {
                select.value = matchingOption.value;
                
                // Dispatch change event
                select.dispatchEvent(new Event('change', { bubbles: true }));
                
                console.debug('[variant-cards] Synced native variant select', {
                  index,
                  option: optionValue,
                  selectId: select.id || index
                });
              } else {
                console.warn('[variant-cards] Could not find matching option for', {
                  optionValue,
                  availableOptions: Array.from(select.options).map(o => o.value)
                });
              }
            }
          });
          
          // Dispatch change event on the entire variant-selects element
          variantSelects.dispatchEvent(new Event('change', { bubbles: true }));
          
          // Also trigger selling plan update after variant change
          setTimeout(() => {
            // Check if there's a selling plan selected and sync it
            const stickyATC = document.querySelector('sticky-atc-bar');
            if (stickyATC) {
              this.syncRechargeSellingPlan(stickyATC);
            }
          }, 300);
        } catch (e) {
          console.error('[variant-cards] Error syncing with native variant selects:', e);
        }
      }
      
      /**
       * Syncs Recharge selling plan between main form and sticky ATC
       * @param {Element} stickyATC - The sticky ATC element
       */
      syncRechargeSellingPlan(stickyATC) {
        try {
          // Find the main form's selling plan input
          const mainForm = document.querySelector(`#product-form-${this.sectionId}`);
          if (!mainForm) {
            console.warn('[variant-cards] Main product form not found for selling plan sync');
            return;
          }
          
          const mainSellingPlanInput = mainForm.querySelector('[name="selling_plan"]');
          if (!mainSellingPlanInput) {
            console.debug('[variant-cards] No selling plan input found in main form');
            return;
          }
          
          // Find the sticky ATC form
          const stickyForm = stickyATC.querySelector('form');
          if (!stickyForm) {
            console.warn('[variant-cards] Sticky ATC form not found');
            return;
          }
          
          // Find or create the selling plan input in the sticky form
          let stickySellingPlanInput = stickyForm.querySelector('[name="selling_plan"]');
          
          if (!stickySellingPlanInput) {
            stickySellingPlanInput = document.createElement('input');
            stickySellingPlanInput.type = 'hidden';
            stickySellingPlanInput.name = 'selling_plan';
            stickyForm.appendChild(stickySellingPlanInput);
            console.debug('[variant-cards] Created selling plan input in sticky ATC form');
          }
          
          // Update the value
          stickySellingPlanInput.value = mainSellingPlanInput.value;
          
          // Keep track of whether the selling plan was removed (switched to one-time purchase)
          const wasSellingPlanRemoved = stickySellingPlanInput.value === '' && 
            stickySellingPlanInput.previousValue && stickySellingPlanInput.previousValue !== '';
          
          // Store the previous value for future reference
          stickySellingPlanInput.previousValue = stickySellingPlanInput.value;
          
          console.debug('[variant-cards] One-time sync of Recharge selling plan', {
            sellingPlan: mainSellingPlanInput.value || '(empty)',
            fromForm: mainForm.id || 'main-form',
            toForm: stickyForm.id || 'sticky-form',
            wasRemoved: wasSellingPlanRemoved
          });
          
          // Explicitly trigger updatePriceFromSellingPlan if available
          if (typeof stickyATC.updatePriceFromSellingPlan === 'function') {
            // Use setTimeout to ensure the input value change is processed first
            setTimeout(() => {
              console.debug('[variant-cards] Directly calling updatePriceFromSellingPlan after sync');
              stickyATC.updatePriceFromSellingPlan();
              
              // Special handling for when selling plan is removed (one-time purchase)
              if (wasSellingPlanRemoved) {
                console.debug('[variant-cards] Selling plan was removed, ensuring price display is updated');
                
                // Call updatePrice instead of manually manipulating classes
                // This ensures proper handling of compare-at-price discounts
                if (typeof stickyATC.updatePrice === 'function') {
                  console.debug('[variant-cards] Calling updatePrice to reset display');
                  stickyATC.updatePrice();
                } else {
                  // Fallback to manual update if updatePrice isn't available
                  const priceWrapper = stickyATC.querySelector('.f-price');
                  
                  // Get current variant to check if it has a regular discount
                  const variantId = stickyATC.querySelector('[name="id"]')?.value;
                  const productData = window.ProductJSON || (window.ReChargeProductJSON || {});
                  const currentVariant = productData.variants?.find(v => String(v.id) === String(variantId));
                  
                  if (currentVariant && currentVariant.compare_at_price > currentVariant.price) {
                    // Product has a regular discount, should show on-sale
                    if (priceWrapper) {
                      priceWrapper.classList.add('f-price--on-sale');
                      console.debug('[variant-cards] Added on-sale class for regular product discount');
                    }
                  } else {
                    // No regular discount, remove on-sale class
                    if (priceWrapper) {
                      priceWrapper.classList.remove('f-price--on-sale');
                      console.debug('[variant-cards] Removed on-sale class for one-time purchase');
                    }
                  }
                }
                
                // Ensure the price is updated again after a short delay
                setTimeout(() => {
                  stickyATC.updatePriceFromSellingPlan();
                }, 300);
              }
            }, 100);
          }
        } catch (e) {
          console.error('[variant-cards] Error in syncRechargeSellingPlan:', e);
        }
      }
      
      /**
       * Updates the sticky ATC bar directly
       * @param {Object} variantData - The complete variant data
       */
      updateStickyATC(variantData) {
        if (!variantData) return;
        
        try {
          // Find the sticky ATC bar
          const stickyATC = document.querySelector('sticky-atc-bar');
          if (!stickyATC) {
            console.debug('[variant-cards] Sticky ATC bar not found');
            return;
          }
          
          // Get the selected selling plan BEFORE changing the variant
          // This is critical for ensuring the selling plan persists across variant changes
          const mainForm = document.querySelector(`#product-form-${this.sectionId}`);
          const mainSellingPlanInput = mainForm?.querySelector('[name="selling_plan"]');
          const sellingPlanValue = mainSellingPlanInput?.value;
          
          console.debug('[variant-cards] Preserving selling plan during variant change:', {
            sellingPlan: sellingPlanValue || '(none)',
            newVariantId: variantData.id
          });
          
          // Update the variant input in the sticky ATC
          const variantInput = stickyATC.querySelector('[name="id"]');
          if (variantInput) {
            variantInput.value = variantData.id;
            
            // Find and sync the selling plan BEFORE triggering the change event
            // This ensures the correct selling plan is set when the variant changes
            const stickyForm = stickyATC.querySelector('form');
            if (stickyForm && sellingPlanValue) {
              let stickySellingPlanInput = stickyForm.querySelector('[name="selling_plan"]');
              
              if (!stickySellingPlanInput) {
                stickySellingPlanInput = document.createElement('input');
                stickySellingPlanInput.type = 'hidden';
                stickySellingPlanInput.name = 'selling_plan';
                stickyForm.appendChild(stickySellingPlanInput);
              }
              
              // Set the selling plan value
              stickySellingPlanInput.value = sellingPlanValue;
              console.debug('[variant-cards] Set selling plan in sticky form before variant change');
            }
            
            // Try to trigger the change event to update the sticky ATC state
            try {
              // Create and dispatch a change event
              const changeEvent = new Event('change', { bubbles: true });
              variantInput.dispatchEvent(changeEvent);
              
              console.debug('[variant-cards] Dispatched change event to sticky ATC variant input', {
                variantId: variantData.id,
                available: variantData.available
              });
              
              // Force an additional call to updatePriceFromSellingPlan after a delay
              // This helps ensure the subscription price is applied after variant changes
              setTimeout(() => {
                if (stickyATC && typeof stickyATC.updatePriceFromSellingPlan === 'function') {
                  console.debug('[variant-cards] Forcing updatePriceFromSellingPlan after variant change');
                  stickyATC.updatePriceFromSellingPlan();
                }
              }, 1000);
              
              // Now try to directly update the button state by accessing the sticky ATC methods
              if (typeof stickyATC.updateButton === 'function') {
                // Call updateButton with correct availability
                stickyATC.updateButton(!variantData.available, 
                  variantData.available ? FoxTheme.variantStrings.addToCart : FoxTheme.variantStrings.soldOut);
                
                console.debug('[variant-cards] Directly called updateButton on sticky ATC', {
                  disabled: !variantData.available,
                  text: variantData.available ? 'Add to Cart' : 'Sold Out'
                });
              }
            } catch (eventError) {
              console.warn('[variant-cards] Error dispatching change event to sticky ATC:', eventError);
            }
          } else {
            console.warn('[variant-cards] Variant input not found in sticky ATC');
          }
          
          // Find and update Recharge selling plan if present
          this.syncRechargeSellingPlan(stickyATC);
        } catch (e) {
          console.error('Error in updateStickyATC:', e);
        }
      }

      updateBrowserHistory(variantId) {
        if (!this.updateUrl) return;
        
        const productUrl = this.dataset.url || window.location.pathname;
        const searchParams = new URLSearchParams(window.location.search);
        
        searchParams.set('variant', variantId);
        window.history.replaceState({ path: window.location.pathname }, '', `${productUrl}?${searchParams.toString()}`);
      }
    }
  );
}
